import AbstractText from "./AbstractText";
import {TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../constants";
import {IRectCss, ITextRange} from "./index";
import {ISyncField} from "../../index";
import {nsDynamicVariable} from "./nsDynamicVariable";

export default abstract class AbstractInput extends AbstractText<HTMLTextAreaElement> {

    protected override onViewReady() {
        super.onViewReady();
        this.initListeners();
        document.body.appendChild(this.shadow);
    }

    private initListeners() {
        jQuery(window).on('resize scroll', this.onEventHandler);
        jQuery(this.getEl()).on('keyup keydown', this.onKeyHandler);
        jQuery(this.getEl()).on('focusin click paste cut NodeChange resize input blur focusout scroll', this.onEventHandler);
    }

    protected override onKey(e: KeyboardEvent): boolean {
        switch (e.key) {
            case TEXT_WRAPPER_START:
                if (e.type == 'keydown') {
                    const position = this.srcEl.selectionStart + 1;
                    this.replaceSelectedText(this.srcEl.selectionStart, this.srcEl.selectionEnd, `${TEXT_WRAPPER_START}${TEXT_WRAPPER_END}`);
                    this.setCaretPosition(position);
                    e.preventDefault();
                }
                break;
        }
        return super.onKey(e);
    }

    protected updateParamsExpression() {
        // void
    }

    protected showList(textRange: ITextRange, textBounds: ClientRect): void {
        const viewportBounds = document.documentElement.getBoundingClientRect();

        const sl = document.documentElement.scrollLeft;
        const st = document.documentElement.scrollTop;

        const l = textBounds.left + sl;
        const t = textBounds.top + st;

        this.highlight.rect({
            left: l + 'px',
            top: t + 'px',
            width: textBounds.width + 'px',
            height: textBounds.height + 'px',
            bottom: null,
            right: null,
        });

        const rect: IRectCss = {
            top: null,
            left: null,
            right: null,
            bottom: null,
            width: '500px',
            height: 'auto',
        };

        if (l <= viewportBounds.width / 2 + sl) {
            const l = textBounds.left + sl;
            rect.left = Math.max(l, sl) + 'px';
        } else {
            const r = viewportBounds.right - textBounds.right;
            rect.right = Math.max(r, -sl) + 'px';
        }
        if (t <= viewportBounds.height / 2 + st) {
            const t = textBounds.top + st + textBounds.height;
            rect.top = Math.max(t, st) + 'px';
        } else {
            const b = viewportBounds.bottom - textBounds.bottom + textBounds.height;
            rect.bottom = Math.max(b, -st) + 'px';
        }

        this.list.show({styles: rect, textRange: textRange});
    }

    protected hasFocus() {
        return this.srcEl === document.activeElement;
    }

    protected override getEl(): HTMLTextAreaElement {
        return super.getEl() as HTMLTextAreaElement;
    }

    public getTextRange(): ITextRange | null {
        return nsDynamicVariable.getTextRange(this.srcEl.selectionStart, this.srcEl.value);
    }

    protected getBoundsAndRange(): { selectionRange: Range, textRange: ITextRange, textBounds: ClientRect } | null {

        try {
            this.shadow.textContent = this.srcEl.value;
        } catch (err) {
            return null;
        }

        const rect = this.srcEl.getBoundingClientRect();
        const sl = document.documentElement.scrollLeft;
        const st = document.documentElement.scrollTop;

        this.shadow.style.top = (rect.top + st) + "px";
        this.shadow.style.left = (rect.left + sl) + "px";
        this.shadow.style.width = rect.width + "px";
        this.shadow.style.height = rect.height + "px";
        this.shadow.scrollTop = this.srcEl.scrollTop;
        this.shadow.scrollLeft = this.srcEl.scrollLeft;

        const textRange = this.getTextRange();

        if (!textRange) {
            return null;
        }

        const selectionRange = document.createRange();
        const shadowText = this.shadow.firstChild;
        if (!shadowText) {
            return null;
        }
        selectionRange.setStart(shadowText, textRange.start - 1);
        selectionRange.setEnd(shadowText, textRange.end + 1);

        return {
            selectionRange: selectionRange,
            textRange: textRange,
            textBounds: selectionRange.getBoundingClientRect(),
        }
    }

    protected insertVariable(syncfield: ISyncField) {
        const variableNameObject = this.getVariableNameObject(syncfield);
        const bounds = this.getBoundsAndRange();
        if (!bounds) {
            return;
        }
        const {textRange} = bounds;
        const displayName = variableNameObject.isFunction ? variableNameObject.displayname ?? variableNameObject.raw : variableNameObject.raw;
        this.replaceSelectedText(textRange.start, textRange.end, displayName);

        if (variableNameObject.isFunction && variableNameObject.delimiterIndexes.length) {
            const firstDelimiter = variableNameObject.delimiterIndexes[0];
            if (firstDelimiter === undefined) {
                return;
            }
            const sDelimiter = textRange.start + firstDelimiter;
            const eDelimiter = textRange.start + (variableNameObject.delimiterIndexes[1] ? variableNameObject.delimiterIndexes[1] - 1 : displayName.length);
            this.srcEl.selectionStart = sDelimiter;
            this.srcEl.selectionEnd = eDelimiter;
        } else {
            this.setCaretPosition(textRange.start + displayName.length + 1);
        }

        this.onEvent();
    }

    protected insertParameter(syncfield: ISyncField, value: string, index: number): void {
    }

    public insertString(str: string) {
        const bounds = this.getBoundsAndRange();
        if (!bounds) {
            return;
        }
        const {textRange} = bounds;
        this.replaceSelectedText(textRange.start, textRange.end, str);
        this.setCaretPosition(textRange.start + str.length);
        this.updateParamsExpression();
    }

    /** Replaces a character range via the browser undo stack (Ctrl+Z / Ctrl+Y). */
    protected replaceSelectedText(start: number, end: number, text: string): void {
        const el = this.srcEl;
        el.focus();
        el.setSelectionRange(start, end);
        if (!document.execCommand('insertText', false, text)) {
            const val = el.value;
            el.value = val.substring(0, start) + text + val.substring(end);
            el.dispatchEvent(new Event('input', {bubbles: true}));
        }
    }

    protected getDocument() {
        return document;
    }

    protected setCaretPosition(position: number) {
        this.srcEl.selectionStart = position;
        this.srcEl.selectionEnd = position;
    }

    public addEventListener(event: string, handler: () => void): void {
        this.getEl()?.addEventListener(event, handler);
    }
    public removeEventListener(event: string, handler: () => void): void {
        this.getEl()?.removeEventListener(event, handler);
    }

    override dispose() {
        super.dispose();
        try {
            document.body.removeChild(this.shadow);
        } catch (err) {
        }
        jQuery(window)?.off('resize scroll', this.onEventHandler);
        jQuery(this.getEl())?.off('keyup keydown', this.onKeyHandler);
        jQuery(this.getEl())?.off('focus click paste cut NodeChange resize input blur focusout scroll', this.onEventHandler);
    }
}
